/**
 * SnsLoginPage — A-2. 스튜디오 입장하기 (로그인, 기본 진입 화면)
 * Layout: D-07 §21.5 L9 인증 2분할 — 골격은 AuthLayout 이 소유한다.
 * ID/PW 로그인과 SNS 로그인을 한 화면에 통합 — ID 섹션이 위, SNS 섹션이 아래(기획서 순서).
 */
import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore, SESSION_KICK_FLAG } from '@indipia/shared';
import { Alert, Button, Input } from '@indipia/ui-core';
import { AuthLayout } from '../../app/layouts/AuthLayout';
import { SnsBtnGroup } from '../components/SnsBtn';
import { OrDivider } from '../components/OrDivider';
import { PasswordField } from '../components/PasswordField';
import { useSnsAuthPopup } from '../hooks/useSnsAuthPopup';
import { loginWithIdPassword } from '../api/auth';
import { applyLoginTokens } from '../lib/post-login';

const schema = z.object({
  loginId:  z.string().min(1, '아이디를 입력해 주세요.'),
  password: z.string().min(1, '비밀번호를 입력해 주세요.'),
});
type FormValues = z.infer<typeof schema>;

const SECTION_LABEL_CLASS = 'block text-caption font-bold uppercase tracking-widest text-text-disabled';

const FOOTER_LINKS = [
  { to: '/signup',  label: '회원가입' },
  { to: '/find-id', label: '아이디 찾기' },
  { to: '/find-pw', label: '비밀번호 찾기' },
];

export function SnsLoginPage() {
  const location      = useLocation();
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();
  const setAuth       = useAuthStore((s) => s.setAuth);

  const routeState  = location.state as { snsError?: string; signupDone?: boolean; pwChanged?: boolean } | null;
  const routeError  = routeState?.snsError  ?? null;
  const signupDone  = routeState?.signupDone ?? false;
  const pwChanged   = routeState?.pwChanged  ?? false;

  // 다른 기기 로그인에 의한 세션 축출 안내 — API 클라이언트가 하드 로그아웃 시 세운 플래그를 1회 읽고 지운다.
  const [sessionKicked] = useState(() => {
    try {
      if (sessionStorage.getItem(SESSION_KICK_FLAG) === '1') {
        sessionStorage.removeItem(SESSION_KICK_FLAG);
        return true;
      }
    } catch {
      /* sessionStorage 접근 불가 — 안내 생략 */
    }
    return false;
  });

  const { loadingProvider, snsError: hookError, handleSns, anyLoading } = useSnsAuthPopup();
  const [localError, setLocalError] = useState<string | null>(routeError);
  const snsError = hookError ?? localError;

  // ── ID/PW 로그인 폼 ───────────────────────────────────────────────────
  const [isLoading,  setIsLoading]  = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    setLoginError(null);
    try {
      const tokens = await loginWithIdPassword(data.loginId, data.password);
      if (!tokens?.accessToken) {
        setLoginError('로그인 응답이 올바르지 않습니다.');
        return;
      }
      applyLoginTokens(tokens, setAuth, queryClient);
      void navigate('/app');
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      const status = err?.response?.status;
      if (status == null) {
        setLoginError('서버에 연결하지 못했습니다. 네트워크 상태를 확인해 주세요.');
      } else if (status === 401) {
        setLoginError('아이디 또는 비밀번호가 올바르지 않습니다.');
      } else if (status === 429) {
        setLoginError('요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.');
      } else {
        const msg = err?.response?.data?.message;
        setLoginError(msg || '로그인에 실패했습니다. 다시 시도해 주세요.');
      }
      console.error('[login]', status, err?.response?.data ?? err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      kicker="Member Login"
      title="스튜디오 입장하기"
      subtitle="인디피아에 오신 걸 환영해요."
      banner={
        sessionKicked ? (
          <Alert kind="error">다른 기기에서 로그인되어 로그아웃되었습니다.</Alert>
        ) : (signupDone || pwChanged) ? (
          <Alert kind="success">
            {pwChanged
              ? '비밀번호가 변경됐어요. 새 비밀번호로 로그인해 주세요.'
              : '회원가입이 완료됐어요. 로그인 후 이용해 주세요.'}
          </Alert>
        ) : undefined
      }
    >
      {/* ID로 스튜디오 입장하기 */}
      <span className={SECTION_LABEL_CLASS}>ID로 스튜디오 입장하기</span>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-03 flex flex-col gap-md">
        <Input
          label="아이디"
          width="fill"
          size="lg"
          type="text"
          autoComplete="username"
          placeholder="아이디를 입력하세요"
          error={errors.loginId?.message}
          {...register('loginId')}
        />

        <PasswordField
          label="비밀번호"
          size="lg"
          autoComplete="current-password"
          placeholder="비밀번호를 입력하세요"
          error={errors.password?.message}
          {...register('password')}
        />

        {loginError && <Alert kind="error">{loginError}</Alert>}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isLoading}>
          스튜디오 입장하기
        </Button>
      </form>

      <OrDivider />

      {/* SNS로 스튜디오 입장하기 */}
      <span className={SECTION_LABEL_CLASS}>SNS로 스튜디오 입장하기</span>

      <div className="mt-03 flex flex-col gap-md">
        {snsError && <Alert kind="error">{snsError}</Alert>}

        <div>
          <SnsBtnGroup
            onClick={(provider) => { setLocalError(null); void handleSns(provider); }}
            loadingProvider={loadingProvider}
            anyLoading={anyLoading}
            labelSuffix="스튜디오 입장하기"
          />
        </div>
      </div>

      {/* 링크 행 — 회원가입 · 아이디 찾기 · 비밀번호 찾기 */}
      <div className="mt-06 flex flex-wrap items-center justify-center">
        {FOOTER_LINKS.map(({ to, label }, i, arr) => (
          <span key={to} className="inline-flex items-center">
            <Link
              to={to}
              className="whitespace-nowrap border-b border-border pb-px text-body-sm font-semibold text-text-secondary transition-colors hover:text-point"
            >
              {label}
            </Link>
            {i < arr.length - 1 && (
              <span className="mx-03 select-none text-caption text-text-disabled">·</span>
            )}
          </span>
        ))}
      </div>
    </AuthLayout>
  );
}
